import { TruncatedText } from "@/components/ui/truncation-tooltip";
import type { LlmBlockDetail } from "@/services/api";
import { llmReviewMessages, llmReviewReasonMessages } from "@/services/messages";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, type ReactNode } from "react";

interface BlocksTableProps {
	blocks: LlmBlockDetail[];
	expandedIds: Set<number>;
	onToggle: (blockId: number) => void;
	renderExpand: (block: LlmBlockDetail) => ReactNode;
}

const DATE_FMT = new Intl.DateTimeFormat("ru-RU", {
	dateStyle: "short",
	timeStyle: "short",
});

function formatAt(value: string | null): string {
	if (!value) return "—";
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return value;
	return DATE_FMT.format(d);
}

/**
 * Badge цвета по статусу + reason-aware:
 *   - skipped с no_coded_line  → slate (штатно)
 *   - skipped с другим reason  → amber (с замечаниями)
 *   - applied → green, failed → red, pending → blue (spin), none → slate-light.
 */
function statusBadgeClasses(block: LlmBlockDetail): string {
	switch (block.llmReviewStatus) {
		case "applied":
			return "bg-emerald-100 text-emerald-800 border-emerald-200";
		case "failed":
			return "bg-red-100 text-red-800 border-red-200";
		case "pending":
			return "bg-blue-100 text-blue-800 border-blue-200";
		case "skipped":
			return block.llmReviewReason === "no_coded_line"
				? "bg-slate-100 text-slate-600 border-slate-200"
				: "bg-amber-100 text-amber-800 border-amber-200";
		default:
			return "bg-white text-slate-500 border-slate-200";
	}
}

function statusLabel(status: LlmBlockDetail["llmReviewStatus"]): string {
	const m = llmReviewMessages;
	switch (status) {
		case "none":
			return m.status_none;
		case "pending":
			return m.status_pending;
		case "applied":
			return m.status_applied;
		case "skipped":
			return m.status_skipped;
		case "failed":
			return m.status_failed;
	}
}

function sectionHeaderText(b: LlmBlockDetail): string {
	const trimmed = b.sectionHeader?.trim();
	if (trimmed) return trimmed;
	return llmReviewMessages.block_no_header;
}

export function BlocksTable({
	blocks,
	expandedIds,
	onToggle,
	renderExpand,
}: BlocksTableProps): React.JSX.Element {
	const m = llmReviewMessages;
	return (
		<div className="overflow-hidden rounded-lg border border-slate-200">
			<table className="w-full text-sm">
				<thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
					<tr>
						<th className="w-10 px-2 py-2" />
						<th className="w-14 px-2 py-2">{m.table_col_block}</th>
						<th className="px-2 py-2">{m.table_col_section}</th>
						<th className="w-44 px-2 py-2">{m.table_col_status}</th>
						<th className="px-2 py-2">{m.table_col_reason}</th>
						<th className="w-36 px-2 py-2">{m.table_col_at}</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-slate-200">
					{blocks.map((b) => {
						const isExpandable = b.hasReviewJson;
						const isExpanded = expandedIds.has(b.blockId);
						const reasonText = b.llmReviewReason
							? (llmReviewReasonMessages[b.llmReviewReason] ?? b.llmReviewReason)
							: "—";
						const onRowClick = isExpandable ? () => onToggle(b.blockId) : undefined;
						return (
							<Fragment key={b.blockId}>
								<tr
									className={`text-slate-800 ${
										isExpandable ? "cursor-pointer hover:bg-slate-50" : ""
									}`}
									// role="button" нужен для screen-reader'ов — без него <tr> с onClick
									// воспринимается как обычная ячейка таблицы, и aria-expanded
									// игнорируется (a11y H2).
									role={isExpandable ? "button" : undefined}
									onClick={onRowClick}
									onKeyDown={(e) => {
										if (isExpandable && (e.key === "Enter" || e.key === " ")) {
											e.preventDefault();
											onToggle(b.blockId);
										}
									}}
									tabIndex={isExpandable ? 0 : undefined}
									aria-expanded={isExpandable ? isExpanded : undefined}
								>
									<td className="px-2 py-2 align-top">
										{isExpandable ? (
											isExpanded ? (
												<ChevronDown className="h-4 w-4 text-slate-500" aria-hidden="true" />
											) : (
												<ChevronRight className="h-4 w-4 text-slate-400" aria-hidden="true" />
											)
										) : null}
									</td>
									<td className="px-2 py-2 align-top font-medium tabular-nums">{b.blockNumber}</td>
									<td className="max-w-md px-2 py-2 align-top">
										<TruncatedText value={sectionHeaderText(b)} />
									</td>
									<td className="px-2 py-2 align-top">
										<span
											className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${statusBadgeClasses(b)}`}
										>
											{statusLabel(b.llmReviewStatus)}
										</span>
									</td>
									<td className="px-2 py-2 align-top text-slate-600">
										<TruncatedText value={reasonText} />
									</td>
									<td className="px-2 py-2 align-top text-xs text-slate-500 tabular-nums">
										{formatAt(b.llmReviewAt)}
									</td>
								</tr>
								{isExpanded && (
									<tr className="bg-slate-50">
										<td />
										<td colSpan={5} className="px-2 py-3">
											{renderExpand(b)}
										</td>
									</tr>
								)}
							</Fragment>
						);
					})}
				</tbody>
			</table>
			<div className="px-2 py-1.5 text-[11px] text-slate-400">{m.expand_hint}</div>
		</div>
	);
}
